/** @jsxImportSource @emotion/react */
import {
  Typography,
  createStyles,
  Icon,
  Card,
  useTheme,
} from '@quickbit/qb-design-system';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../context/Session';
import { useTabs } from '../../context/Tabs';
import { formatRoutePath, hasPermissions } from '../../utils';

export const Home = () => {
  const styles = useStyles();
  const { spacing } = useTheme();
  const { tabs } = useTabs();
  const session = useSession();
  const navigate = useNavigate();

  const canShowTab = (tabName: string) => {
    if (!session.authenticated) return false;
    const tab = tabs.find((t) => t.name === tabName);
    if (!tab) return false;
    if (tab.name === 'Home') return false;
    return hasPermissions(session.permissions, tab.requiredPermissions);
  };

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <Typography variant="subtitle">Home</Typography>
      </div>
      <div css={styles.content}>
        {tabs.map((tab) =>
          canShowTab(tab.name) ? (
            <div
              key={tab.name}
              onClick={() =>
                navigate(`/${formatRoutePath(tab.name)}`, { replace: true })
              }
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  void navigate(`/${formatRoutePath(tab.name)}`, {
                    replace: true,
                  });
                }
              }}
              css={styles.card}
            >
              <Card>
                <div css={styles.cardContent}>
                  <Icon
                    name={tab.icon}
                    size={spacing.l}
                    className={styles.icon}
                  />
                  <Typography variant="subtitle">{tab.name}</Typography>
                </div>
              </Card>
            </div>
          ) : null
        )}
      </div>
    </div>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    padding: theme.spacing.xxs,
    background: theme.palette.white,
    border: `1px solid ${theme.palette.grey.x100}`,
    borderRadius: theme.shape.borderRadius,
  },
  content: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gridTemplateRows: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: theme.spacing.s,
    marginTop: theme.spacing.xxs,
    padding: theme.spacing.xs,
  },
  card: {
    width: '200px',
    aspectRatio: 1,
    cursor: 'pointer',
    flex: 1,
  },
  cardContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.spacing.xs,
  },
  icon: {
    '& svg': {
      color: theme.palette.grey.default,
    },
  },
}));
